import { useEffect, useState } from 'react'
import { deburr } from 'lodash-es'

/**
 * - departements: French départements (INSEE codes)
 * - regions: a country's top-level subdivisions, as Google Places' administrative_area_level_1 (French regions use
 *   INSEE codes, other European countries ISO 3166-2 codes)
 * - countries: world countries (lowercase ISO 3166-1 alpha-2 codes)
 */
export type BoundaryKind = 'departements' | 'regions' | 'countries'

/** GeoJSON position: [longitude, latitude] */
type Position = [number, number]
type PolygonCoordinates = Position[][]

export interface BoundaryFeature {
  type: 'Feature'
  /** `names`: other names Google may return for it (English, French, local or official forms) */
  properties: { code: string; nom: string; names?: string[] }
  geometry:
    | { type: 'Polygon'; coordinates: PolygonCoordinates }
    | { type: 'MultiPolygon'; coordinates: PolygonCoordinates[] }
}

export interface BoundaryCollection {
  type: 'FeatureCollection'
  features: BoundaryFeature[]
}

interface Bounds {
  west: number
  south: number
  east: number
  north: number
}

/** A stored département or region: a name and the Google viewport saved when it was added */
export interface TargetingZoneLike {
  name?: string | null
  geometry?: {
    southwest?: { latitude?: number | null; longitude?: number | null } | null
    northeast?: { latitude?: number | null; longitude?: number | null } | null
  } | null
}

/** European markets with a region file in public/geo/regions (France uses its own INSEE file) */
const REGION_COUNTRIES = new Set([
  'ad', 'al', 'at', 'ax', 'ba', 'be', 'bg', 'by', 'ch', 'cy', 'cz', 'de', 'dk', 'ee', 'es', 'fi', 'fo',
  'gb', 'gg', 'gi', 'gr', 'hr', 'hu', 'ie', 'im', 'is', 'it', 'je', 'li', 'lt', 'lu', 'lv', 'mc', 'md',
  'me', 'mk', 'mt', 'nl', 'no', 'pl', 'pt', 'ro', 'rs', 'ru', 'se', 'si', 'sk', 'sm', 'ua', 'va', 'xk',
]) // prettier-ignore

/** Where the boundaries of a kind live for a market, or undefined when there are none */
export function getBoundaryUrl(kind: BoundaryKind, country?: string): string | undefined {
  const market = country?.toLowerCase()
  switch (kind) {
    case 'countries':
      return '/geo/world-countries.geojson'
    case 'departements':
      return market === 'fr' ? '/geo/fr-departements.geojson' : undefined
    case 'regions':
      if (market === 'fr') return '/geo/fr-regions.geojson'
      return market && REGION_COUNTRIES.has(market) ? `/geo/regions/${market}.geojson` : undefined
  }
}

export const hasBoundaries = (kind: BoundaryKind, country?: string): boolean =>
  getBoundaryUrl(kind, country) !== undefined

const boundariesCache = new Map<string, Promise<BoundaryCollection>>()

export function loadBoundaries(kind: BoundaryKind, country?: string): Promise<BoundaryCollection> {
  const url = getBoundaryUrl(kind, country)
  if (!url) return Promise.reject(new Error(`No ${kind} boundaries for ${country ?? 'this market'}`))

  const cached = boundariesCache.get(url)
  if (cached) return cached

  const request = fetch(url).then((response) => {
    if (!response.ok) throw new Error(`Unable to load ${url} (${response.status})`)
    return response.json() as Promise<BoundaryCollection>
  })
  // Let a failed download be retried on the next call
  request.catch(() => boundariesCache.delete(url))
  boundariesCache.set(url, request)
  return request
}

export function clearBoundariesCache(): void {
  boundariesCache.clear()
}

/**
 * Loads the boundaries of a kind for a market, lazily: nothing is fetched until `enabled` is true,
 * and nothing at all when the market has no such boundaries.
 */
export function useBoundaries(
  kind: BoundaryKind | undefined,
  country: string | undefined,
  enabled: boolean
): BoundaryFeature[] | undefined {
  const url = kind ? getBoundaryUrl(kind, country) : undefined
  const [loaded, setLoaded] = useState<{ url: string; features: BoundaryFeature[] }>()

  useEffect(() => {
    if (!enabled || !kind || !url) return undefined
    let active = true
    loadBoundaries(kind, country)
      .then((collection) => {
        if (active) setLoaded({ url, features: collection.features })
      })
      .catch(() => undefined)
    return () => {
      active = false
    }
  }, [kind, country, url, enabled])

  return enabled && loaded && loaded.url === url ? loaded.features : undefined
}

// Google Places may return English names depending on the browser language
const NAME_ALIASES: Record<string, string> = {
  brittany: 'bretagne',
  normandy: 'normandie',
  corsica: 'corse',
  occitania: 'occitanie',
  'burgundy franche comte': 'bourgogne franche comte',
  'south corsica': 'corse du sud',
  'upper corsica': 'haute corse',
}

export function normalizeBoundaryName(name: string): string {
  const normalized = deburr(name)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
    .replace(/^(departement|region)( (de la|de l|du|des|de|d))? /, '')
  return NAME_ALIASES[normalized] ?? normalized
}

const getPolygons = (feature: BoundaryFeature): PolygonCoordinates[] =>
  feature.geometry.type === 'Polygon' ? [feature.geometry.coordinates] : feature.geometry.coordinates

const boundsCache = new WeakMap<BoundaryFeature, Bounds>()

export function getFeatureBounds(feature: BoundaryFeature): Bounds {
  const cached = boundsCache.get(feature)
  if (cached) return cached

  const bounds = { west: Infinity, south: Infinity, east: -Infinity, north: -Infinity }
  getPolygons(feature).forEach((polygon) =>
    polygon[0].forEach(([longitude, latitude]) => {
      bounds.west = Math.min(bounds.west, longitude)
      bounds.east = Math.max(bounds.east, longitude)
      bounds.south = Math.min(bounds.south, latitude)
      bounds.north = Math.max(bounds.north, latitude)
    })
  )
  boundsCache.set(feature, bounds)
  return bounds
}

const containsPoint = (bounds: Bounds, longitude: number, latitude: number): boolean =>
  longitude >= bounds.west && longitude <= bounds.east && latitude >= bounds.south && latitude <= bounds.north

const pointInRing = (longitude: number, latitude: number, ring: Position[]): boolean => {
  let inside = false
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i]
    const [xj, yj] = ring[j]
    if (yi > latitude !== yj > latitude && longitude < ((xj - xi) * (latitude - yi)) / (yj - yi) + xi) inside = !inside
  }
  return inside
}

export function featureContainsPoint(feature: BoundaryFeature, latitude: number, longitude: number): boolean {
  if (!containsPoint(getFeatureBounds(feature), longitude, latitude)) return false
  return getPolygons(feature).some(
    ([outerRing, ...holes]) =>
      pointInRing(longitude, latitude, outerRing) && !holes.some((hole) => pointInRing(longitude, latitude, hole))
  )
}

export function findFeatureContainingPoint(
  latitude: number,
  longitude: number,
  features: BoundaryFeature[]
): BoundaryFeature | undefined {
  return features.find((feature) => featureContainsPoint(feature, latitude, longitude))
}

const KM_PER_DEGREE = 111.2

/** Distance from a point to a segment, in degrees of latitude, longitudes being scaled beforehand */
const distanceToSegment = (x: number, y: number, [ax, ay]: Position, [bx, by]: Position): number => {
  const dx = bx - ax
  const dy = by - ay
  const lengthSquared = dx * dx + dy * dy
  const t = lengthSquared === 0 ? 0 : Math.max(0, Math.min(1, ((x - ax) * dx + (y - ay) * dy) / lengthSquared))
  return Math.hypot(x - ax - t * dx, y - ay - t * dy)
}

const distanceToFeatureKm = (feature: BoundaryFeature, latitude: number, longitude: number): number => {
  const scale = Math.cos((latitude * Math.PI) / 180)
  let best = Infinity
  getPolygons(feature).forEach((polygon) =>
    polygon.forEach((ring) => {
      for (let i = 1; i < ring.length; i++) {
        const a: Position = [ring[i - 1][0] * scale, ring[i - 1][1]]
        const b: Position = [ring[i][0] * scale, ring[i][1]]
        best = Math.min(best, distanceToSegment(longitude * scale, latitude, a, b))
      }
    })
  )
  return best * KM_PER_DEGREE
}

/**
 * The feature containing the point or, failing that, the closest one within `maxDistanceKm`: the borders are simplified,
 * so a city on the coast (Ajaccio) can fall a few metres out at sea
 */
export function findFeatureNearPoint(
  latitude: number,
  longitude: number,
  features: BoundaryFeature[],
  maxDistanceKm = 2
): BoundaryFeature | undefined {
  const containing = findFeatureContainingPoint(latitude, longitude, features)
  if (containing) return containing

  const margin = maxDistanceKm / KM_PER_DEGREE
  const lonMargin = margin / Math.max(Math.cos((latitude * Math.PI) / 180), 0.01)
  let best: { feature: BoundaryFeature; distance: number } | undefined
  features.forEach((feature) => {
    const { west, south, east, north } = getFeatureBounds(feature)
    if (longitude < west - lonMargin || longitude > east + lonMargin) return
    if (latitude < south - margin || latitude > north + margin) return
    const distance = distanceToFeatureKm(feature, latitude, longitude)
    if (distance <= maxDistanceKm && (!best || distance < best.distance)) best = { feature, distance }
  })
  return best?.feature
}

const namesCache = new WeakMap<BoundaryFeature[], Map<string, BoundaryFeature>>()

const getFeaturesByName = (features: BoundaryFeature[]): Map<string, BoundaryFeature> => {
  const cached = namesCache.get(features)
  if (cached) return cached

  const byName = new Map(features.map((feature) => [normalizeBoundaryName(feature.properties.nom), feature]))
  // Alternative names never take over a feature's own name
  features.forEach((feature) =>
    feature.properties.names?.forEach((name) => {
      const normalized = normalizeBoundaryName(name)
      if (!byName.has(normalized)) byName.set(normalized, feature)
    })
  )
  namesCache.set(features, byName)
  return byName
}

const getZoneBounds = (zone: TargetingZoneLike): Bounds | undefined => {
  const { southwest, northeast } = zone.geometry ?? {}
  const values = [southwest?.longitude, southwest?.latitude, northeast?.longitude, northeast?.latitude]
  if (values.some((value) => value === null || value === undefined)) return undefined
  const [west, south, east, north] = values as number[]
  return { west, south, east, north }
}

const centerOf = (bounds: Bounds): [number, number] => [
  (bounds.west + bounds.east) / 2,
  (bounds.south + bounds.north) / 2,
]

/**
 * Matches a stored zone to its real boundary: by name first, then by the closest bounding box.
 * Returns undefined when nothing matches, so the caller can keep drawing the stored box.
 */
export function findZoneFeature(zone: TargetingZoneLike, features: BoundaryFeature[]): BoundaryFeature | undefined {
  if (zone.name) {
    const byName = getFeaturesByName(features).get(normalizeBoundaryName(zone.name))
    if (byName) return byName
  }

  const zoneBounds = getZoneBounds(zone)
  if (!zoneBounds) return undefined

  let best: { feature: BoundaryFeature; distance: number } | undefined
  features.forEach((feature) => {
    const featureBounds = getFeatureBounds(feature)
    // Both centres must fall inside the other box to count as the same area
    if (!containsPoint(zoneBounds, ...centerOf(featureBounds))) return
    if (!containsPoint(featureBounds, ...centerOf(zoneBounds))) return

    const distance =
      Math.abs(featureBounds.west - zoneBounds.west) +
      Math.abs(featureBounds.east - zoneBounds.east) +
      Math.abs(featureBounds.south - zoneBounds.south) +
      Math.abs(featureBounds.north - zoneBounds.north)
    if (!best || distance < best.distance) best = { feature, distance }
  })
  return best?.feature
}

/** A targeted country: an ISO code from the API, or the { code, label } object the form edits */
export type TargetingCountryLike = string | { code?: string | null; label?: string | null }

const codesCache = new WeakMap<BoundaryFeature[], Map<string, BoundaryFeature>>()

/** Matches a targeted country by its ISO code, whatever its case, then by its English name */
export function findCountryFeature(
  country: TargetingCountryLike,
  features: BoundaryFeature[]
): BoundaryFeature | undefined {
  let byCode = codesCache.get(features)
  if (!byCode) {
    byCode = new Map(features.map((feature) => [feature.properties.code.toLowerCase(), feature]))
    codesCache.set(features, byCode)
  }

  const code = typeof country === 'string' ? country : country.code
  const byCodeMatch = code ? byCode.get(code.toLowerCase()) : undefined
  if (byCodeMatch) return byCodeMatch

  const label = typeof country === 'string' ? undefined : country.label
  return label ? getFeaturesByName(features).get(normalizeBoundaryName(label)) : undefined
}
